import { Card, CardContent, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface StatCardProps {
  label: string;
  value: string;
  /** Tasteful secondary detail (e.g. "12 requested · 3 processing"). */
  secondary?: string;
  /** `alert` paints the card in the error tone — used when something needs action. */
  tone?: 'default' | 'alert';
  testId?: string;
}

/**
 * One stat in the dashboard's card family. Restrained by design: neutral
 * outlined card, an uppercase label, a large number, and optional secondary
 * detail. Color is used MEANINGFULLY — only the `alert` tone (the SLA card when
 * breaches exist) departs from neutral, so the one thing that needs attention is
 * the one thing that stands out.
 */
export const StatCard = ({
  label,
  value,
  secondary,
  tone = 'default',
  testId,
}: StatCardProps) => {
  const alert = tone === 'alert';
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: alert ? 'error.main' : 'divider',
        bgcolor: alert
          ? (theme) => alpha(theme.palette.error.main, 0.06)
          : 'background.paper',
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="overline"
          sx={{
            color: alert ? 'error.main' : 'text.secondary',
            letterSpacing: 0.6,
            lineHeight: 1.4,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h4"
          component="p"
          data-testid={testId}
          sx={{
            fontWeight: 600,
            lineHeight: 1.1,
            color: alert ? 'error.main' : 'text.primary',
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: alert ? 'error.main' : 'text.secondary',
            minHeight: '1.2em',
          }}
        >
          {secondary ?? ''}
        </Typography>
      </CardContent>
    </Card>
  );
};
